import React from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Chip,
  Tooltip,
  Button
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  AccountTree as TreeIcon,
  ViewList as ListIcon,
  ViewModule as CardIcon,
  FolderOpen as DrillIcon,
  Hub as NetworkIcon,
  Add as AddIcon,
  FilterAlt as FilterIcon
} from '@mui/icons-material';
import { useIsMobile } from '../../../hooks/useResponsive';
import { usePermissions } from '../../../contexts/PermissionContext';
import { VIEW_MODES } from '../utils/locationConstants';

/**
 * Location Management page header
 * Responsive: stacks vertically on mobile, compact controls
 */
const LocationHeader = ({
  viewMode,
  searchInputValue,
  typeFilter,
  hasActiveFilters,
  onViewModeChange,
  onSearchChange,
  onResetFilters,
  onAddLocation,
  t
}) => {
  const { hasPermission } = usePermissions();
  const isMobile = useIsMobile();

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: 'divider' }}>
      {/* Title row */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: { xs: 1, sm: 2 }
      }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            className="slide-down"
            sx={{ fontWeight: 600 }}
          >
            {t ? t('LocationManagement.location_management') : 'Location Management'}
          </Typography>
          {!isMobile && (
            <Typography variant="body2" color="text.secondary">
              {t ? t('LocationManagement.manage_your_location_hierarchy_assoc') : 'Manage your location hierarchy and associated devices'}
            </Typography>
          )}
        </Box>

        {hasPermission('locations.create') && (
          isMobile ? (
            <Tooltip title={t ? t('LocationManagement.add_location') : 'Add Location'}>
              <IconButton
                color="primary"
                onClick={() => onAddLocation(null)}
                aria-label={t ? t('LocationManagement.add_location') : 'Add Location'}
                sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => onAddLocation(null)}
            >
              {t ? t('LocationManagement.add_location') : 'Add Location'}
            </Button>
          )
        )}
      </Box>

      {/* Controls row */}
      <Box sx={{
        display: 'flex',
        gap: { xs: 1, sm: 2 },
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* View mode toggle */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => onViewModeChange(newMode)}
          size="small"
          aria-label="View mode"
        >
          <ToggleButton value={VIEW_MODES.TREE} aria-label="Tree view">
            <Tooltip title="Tree View"><TreeIcon /></Tooltip>
          </ToggleButton>
          <ToggleButton value={VIEW_MODES.FLAT_LIST} aria-label="List view">
            <Tooltip title="List View"><ListIcon /></Tooltip>
          </ToggleButton>
          <ToggleButton value={VIEW_MODES.CARD_GRID} aria-label="Card view">
            <Tooltip title="Card View"><CardIcon /></Tooltip>
          </ToggleButton>
          <ToggleButton value={VIEW_MODES.BREADCRUMB_DRILL} aria-label="Browse view">
            <Tooltip title="Browse"><DrillIcon /></Tooltip>
          </ToggleButton>
          {!isMobile && (
            <ToggleButton value={VIEW_MODES.NETWORK} aria-label="Network view">
              <Tooltip title="Network View"><NetworkIcon /></Tooltip>
            </ToggleButton>
          )}
        </ToggleButtonGroup>

        {/* Search field - always visible, full width on mobile */}
        <TextField
          size="small"
          placeholder={t ? t('LocationManagement.search_locations') : 'Search locations...'}
          value={searchInputValue}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            minWidth: { xs: '100%', sm: 250 },
            flex: 1,
            maxWidth: { sm: 400 },
            order: { xs: 3, sm: 0 }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInputValue ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')} aria-label="Clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        {/* Active filters indicator */}
        {hasActiveFilters && (
          <Chip
            icon={<FilterIcon />}
            label={isMobile ? 'Filters' : 'Filters active'}
            onDelete={onResetFilters}
            color="primary"
            variant="outlined"
            size="small"
          />
        )}
      </Box>
    </Box>
  );
};

export default LocationHeader;
